/* @section: home-page */
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, MapPin, Users, Heart, Leaf } from "lucide-react";
import PageLayout from "@/components/Layout";
import { LOGO_URL, orgInfo, missionVision, impactStats, programs, partners, geographicAreas } from "@/data";

const fadeUp = {
  hidden: { opacity: 0, y: 32 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] as [number,number,number,number] } },
};
const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.12 } } };

function Hero() {
  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      <div className="absolute inset-0 z-0" style={{ background: "linear-gradient(135deg, oklch(0.90 0.10 225) 0%, oklch(0.80 0.15 232) 50%, oklch(0.72 0.18 238) 100%)" }} />
      <div className="absolute top-20 right-10 w-80 h-80 rounded-full opacity-25 z-0" style={{ background: "radial-gradient(circle, oklch(0.99 0.01 50) 0%, transparent 70%)" }} />
      <div className="absolute bottom-10 left-10 w-60 h-60 rounded-full opacity-20 z-0" style={{ background: "radial-gradient(circle, oklch(0.99 0.01 210) 0%, transparent 70%)" }} />
      <div className="relative z-10 container mx-auto px-4 sm:px-6 py-24 flex flex-col items-center text-center">
        <motion.div initial={{ opacity: 0, scale: 0.85 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }} className="mb-8">
          <img src={LOGO_URL} alt="FLYN Logo" className="w-28 h-28 md:w-36 md:h-36 rounded-full object-contain bg-white/60 p-3 mx-auto" style={{ boxShadow: "0 0 60px oklch(0.50 0.19 228 / 0.35)" }} />
        </motion.div>
        <motion.div variants={stagger} initial="hidden" animate="visible" className="max-w-4xl">
          <motion.div variants={fadeUp} className="mb-4">
            <span className="inline-block px-4 py-1.5 rounded-full text-xs font-semibold font-mono tracking-wider border border-primary/40 text-primary bg-white/50">
              EST. {orgInfo.foundedYear} · SOUTHERN SHAN STATE, MYANMAR
            </span>
          </motion.div>
          <motion.h1 variants={fadeUp} className="text-4xl md:text-6xl lg:text-7xl font-bold mb-4 leading-tight" style={{ color: "oklch(0.15 0.08 235)" }}>
            Future{" "}
            <span style={{ color: "oklch(0.45 0.20 228)" }}>Light</span>{" "}
            Youth Network
          </motion.h1>
          <motion.p variants={fadeUp} className="text-base md:text-lg mb-2 font-medium" style={{ color: "oklch(0.35 0.12 228)" }}>
            {orgInfo.tagline}
          </motion.p>
          <motion.p variants={fadeUp} className="text-lg md:text-xl max-w-2xl mx-auto mb-10 leading-relaxed" style={{ color: "oklch(0.25 0.06 230)" }}>
            {missionVision.mission}
          </motion.p>
          <motion.div variants={fadeUp} className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/programs" className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl font-semibold text-base transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] text-white" style={{ background: "linear-gradient(135deg, oklch(0.50 0.19 228), oklch(0.40 0.22 235))", boxShadow: "0 8px 30px oklch(0.50 0.19 228 / 0.4)" }}>
              Our Programs <ArrowRight className="w-5 h-5" />
            </Link>
            <Link to="/about" className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl font-semibold text-base border-2 transition-all duration-200 bg-white/50 hover:bg-white/80" style={{ borderColor: "oklch(0.50 0.19 228)", color: "oklch(0.35 0.15 228)" }}>
              About FLYN
            </Link>
          </motion.div>
        </motion.div>
      </div>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.8, duration: 0.8 }} className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1">
        <span className="text-xs font-mono" style={{ color: "oklch(0.40 0.10 228)" }}>scroll</span>
        <motion.div animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.5, ease: "easeInOut" }} className="w-px h-8" style={{ background: "linear-gradient(to bottom, oklch(0.50 0.19 228 / 0.5), transparent)" }} />
      </motion.div>
    </section>
  );
}

function StatsSection() {
  return (
    <section className="py-16 bg-muted/50">
      <div className="container mx-auto px-4 sm:px-6">
        <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true }} className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {impactStats.map((stat) => (
            <motion.div key={stat.label} variants={fadeUp} className="text-center p-5 rounded-2xl bg-background border border-border hover:border-primary/40 transition-all duration-300 hover:shadow-md">
              <p className="text-2xl md:text-3xl font-bold font-mono mb-1" style={{ color: "oklch(0.50 0.19 228)" }}>
                {stat.value}<span style={{ color: "var(--accent)" }}>{stat.suffix}</span>
              </p>
              <p className="text-xs text-muted-foreground font-medium leading-tight">{stat.label}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

function AboutBrief() {
  return (
    <section className="py-20">
      <div className="container mx-auto px-4 sm:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            <motion.div variants={fadeUp}>
              <span className="text-xs font-mono font-semibold text-primary uppercase tracking-widest">About FLYN</span>
              <h2 className="text-3xl md:text-4xl font-bold mt-2 mb-5 leading-tight">Youth-Led. Community-Driven. <span className="text-primary">Impact-Focused.</span></h2>
            </motion.div>
            <motion.p variants={fadeUp} className="text-muted-foreground leading-relaxed mb-4">
              Founded on March 15, 2018 in Pindaya Township, Future Light Youth Network (FLYN) is a registered youth-led civil society organization working across Southern Shan State, Myanmar — delivering humanitarian aid and community development where it matters most.
            </motion.p>
            <motion.p variants={fadeUp} className="text-muted-foreground leading-relaxed mb-6">
              Headquartered in Ywangan Township with a field office in Nyaung Shwe Township, FLYN delivers community-led development and humanitarian assistance to the most vulnerable populations across Southern Shan State.
            </motion.p>
            <motion.div variants={fadeUp} className="flex flex-wrap gap-2 mb-8">
              {["Humanitarian Aid", "Community Development", "Livelihoods", "WASH", "GEDSI", "Climate Resilience"].map((tag) => (
                <span key={tag} className="px-3 py-1 rounded-full text-xs font-medium border border-primary/30 text-primary bg-primary/5">{tag}</span>
              ))}
            </motion.div>
            <motion.div variants={fadeUp}>
              <Link to="/about" className="inline-flex items-center gap-2 text-primary font-semibold text-sm hover:gap-3 transition-all">
                Learn More About Us <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>
          </motion.div>
          <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }} className="grid grid-cols-2 gap-4">
            {[
              { icon: <Users className="w-6 h-6" />, label: "Community Focus", desc: "Grassroots participation in every project" },
              { icon: <Heart className="w-6 h-6" />, label: "Inclusivity (GEDSI)", desc: "Gender equality & social inclusion" },
              { icon: <MapPin className="w-6 h-6" />, label: "3 Townships", desc: "Ywangan · Pindaya · Nyaung Shwe" },
              { icon: <Leaf className="w-6 h-6" />, label: "Sustainability", desc: "Long-term resilience building" },
            ].map((item) => (
              <div key={item.label} className="p-5 rounded-2xl bg-card border border-border hover:border-primary/30 hover:shadow-md transition-all duration-300">
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center text-primary mb-3">{item.icon}</div>
                <p className="font-semibold text-sm mb-1">{item.label}</p>
                <p className="text-xs text-muted-foreground leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function ProgramsPreview() {
  const featured = programs.slice(0, 3);
  return (
    <section className="py-20 bg-muted/30">
      <div className="container mx-auto px-4 sm:px-6">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-12">
          <motion.div variants={fadeUp}>
            <span className="text-xs font-mono font-semibold text-primary uppercase tracking-widest">Our Work</span>
            <h2 className="text-3xl md:text-4xl font-bold mt-2 mb-3">Key Programs & Projects</h2>
            <p className="text-muted-foreground max-w-xl mx-auto">From emergency response to long-term community development, FLYN delivers impact where it matters most.</p>
          </motion.div>
        </motion.div>
        <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true }} className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
          {featured.map((prog) => (
            <motion.div key={prog.id} variants={fadeUp} className="p-6 rounded-2xl bg-background border border-border hover:border-primary/30 hover:shadow-lg transition-all duration-300 flex flex-col">
              <div className="text-3xl mb-4">{prog.icon}</div>
              <div className="flex items-center gap-2 mb-2">
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${prog.status === "Active" ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"}`}>{prog.status}</span>
                <span className="text-xs text-muted-foreground font-mono">{prog.period}</span>
              </div>
              <h3 className="font-bold text-base mb-1">{prog.title}</h3>
              <p className="text-xs text-primary mb-2 font-medium">{prog.titleFull}</p>
              <p className="text-sm text-muted-foreground leading-relaxed mb-4 flex-1">{prog.description}</p>
              <div className="flex items-center justify-between text-xs border-t border-border pt-3">
                <span className="flex items-center gap-1 text-muted-foreground"><MapPin className="w-3 h-3" /> {prog.area}</span>
                <span className="flex items-center gap-1 font-semibold text-primary"><Users className="w-3 h-3" /> {prog.beneficiaries}</span>
              </div>
            </motion.div>
          ))}
        </motion.div>
        <div className="text-center">
          <Link to="/programs" className="inline-flex items-center gap-2 px-6 py-3 rounded-xl border border-primary text-primary font-semibold text-sm hover:bg-primary hover:text-primary-foreground transition-all duration-200">
            View All Programs <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}

function GeographicSection() {
  return (
    <section className="py-20">
      <div className="container mx-auto px-4 sm:px-6">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-12">
          <motion.div variants={fadeUp}>
            <span className="text-xs font-mono font-semibold text-primary uppercase tracking-widest">Where We Work</span>
            <h2 className="text-3xl md:text-4xl font-bold mt-2 mb-3">Our Geographic Coverage</h2>
            <p className="text-muted-foreground max-w-lg mx-auto">Three townships across Southern Shan State, Myanmar</p>
          </motion.div>
        </motion.div>
        <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true }} className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {geographicAreas.map((area, i) => (
            <motion.div key={area.name} variants={fadeUp} className="relative p-7 rounded-2xl border border-border hover:border-primary/40 hover:shadow-lg transition-all duration-300" style={{ background: i === 0 ? "linear-gradient(135deg, oklch(0.97 0.008 170), oklch(0.95 0.005 180))" : "var(--card)" }}>
              {i === 0 && <span className="absolute top-4 right-4 text-xs bg-primary text-primary-foreground px-2 py-0.5 rounded-full font-semibold">HQ</span>}
              {i === 2 && <span className="absolute top-4 right-4 text-xs bg-accent text-accent-foreground px-2 py-0.5 rounded-full font-semibold">Branch</span>}
              <MapPin className="w-8 h-8 text-primary mb-4" />
              <h3 className="font-bold text-lg mb-1">{area.name}</h3>
              <p className="text-sm text-muted-foreground mb-4" style={{ fontFamily: "'Noto Sans Myanmar', sans-serif" }}>{area.nameMM}</p>
              <div className="flex flex-wrap gap-1.5">
                {area.programs.map((p) => (
                  <span key={p} className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary font-medium">{p}</span>
                ))}
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

function PartnersSection() {
  return (
    <section className="py-16 bg-muted/30 border-y border-border">
      <div className="container mx-auto px-4 sm:px-6">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="text-center mb-10">
          <span className="text-xs font-mono font-semibold text-primary uppercase tracking-widest">Partners & Donors</span>
          <h2 className="text-2xl md:text-3xl font-bold mt-2">Our Trusted Partners</h2>
        </motion.div>
        <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true }} className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          {partners.map((p) => (
            <motion.div key={p.name} variants={fadeUp} className="p-4 rounded-xl bg-background border border-border text-center hover:border-primary/40 hover:shadow-md transition-all duration-300">
              <p className="font-semibold text-sm mb-1 leading-tight">{p.name}</p>
              <p className="text-xs text-muted-foreground">{p.country}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

function CTASection() {
  return (
    <section className="py-20">
      <div className="container mx-auto px-4 sm:px-6">
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }} className="relative rounded-3xl overflow-hidden p-10 md:p-16 text-center"           style={{ background: "linear-gradient(135deg, oklch(0.28 0.14 230), oklch(0.22 0.12 240))", boxShadow: "0 30px 80px oklch(0.50 0.19 228 / 0.3)" }}>
          <div className="absolute top-0 right-0 w-72 h-72 rounded-full opacity-10" style={{ background: "radial-gradient(circle, var(--accent) 0%, transparent 70%)" }} />
          <img src={LOGO_URL} alt="FLYN" className="w-16 h-16 rounded-full object-contain bg-white/10 p-2 mx-auto mb-6" />
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Partner With FLYN</h2>
          <p className="text-white/60 max-w-xl mx-auto mb-8 leading-relaxed">Join us in building resilient, inclusive, and self-sufficient communities across Southern Shan State. Together, we can light the way forward.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl font-semibold text-base transition-all duration-200 hover:scale-[1.02]" style={{ background: "linear-gradient(135deg, oklch(0.72 0.14 50), oklch(0.62 0.16 55))", color: "oklch(0.15 0.02 50)", boxShadow: "0 8px 30px oklch(0.72 0.14 50 / 0.4)" }}>
              Get In Touch <ArrowRight className="w-5 h-5" />
            </Link>
            <Link to="/impact" className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl font-semibold text-base border border-white/20 text-white/80 hover:bg-white/10 transition-all">
              View Our Impact
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <PageLayout>
      <Hero />
      <StatsSection />
      <AboutBrief />
      <ProgramsPreview />
      <GeographicSection />
      <PartnersSection />
      <CTASection />
    </PageLayout>
  );
}
